/**
 * Group P — the superadmin DM inbox explorer.
 *
 *     GET /v1/dm-inboxes
 *     GET /v1/dm-inboxes/{user_id}/channels
 *     GET /v1/dm-inboxes/{user_id}/channels/{channel_id}/messages
 *
 * The backend twin is `api/v1/dm_explorer.py`, which is the source of truth for these
 * shapes. Distinct from `@api/v1/members`'s `fetchMemberDms`: that one is the
 * OWNER's own view of one member's conversation, scoped by community; these read across
 * every owner and are superadmin-gated, because a DM inbox belongs to a Skool account
 * rather than to a community.
 */

import { apiGet } from './http';
import type { ListEnvelope } from './envelope';
import type { DmAttachment, DmVideo } from '@ui';


export interface DmInbox {
  user_id: number;
  name: string | null;
  email: string | null;
  role: string | null;
  /** Consent, live off `users.dm_sync_enabled_at`. False WITH channels is a revoke. */
  enabled: boolean;
  enabled_at: string | null;
  channels: number;
  messages: number;
  history_complete_channels: number;
  /** Channels whose counterpart resolved to a member of a community we track. The
   *  remainder are not faults — most conversations come from communities we don't serve. */
  linked_channels: number;
  empty_channels: number;
  first_message_at: string | null;
  last_message_at: string | null;
  last_synced_at: string | null;
  issues: string[];
}

export interface DmCounterpart {
  external_id: string | null;
  handle: string | null;
  name: string | null;
  skool_url: string | null;
  /** Skool avatar, the same key `/v1/me/dms/channels` uses. Required now that #164
   *  ships it on this route too; null is ordinary — `Avatar` falls back to an
   *  initial. */
  picture: string | null;
}

export interface DmChannelRow {
  channel_id: number;
  external_id: string;
  counterpart: DmCounterpart;
  member_id: number | null;
  skool_id: number | null;
  community: string | null;
  messages: number;
  first_message_at: string | null;
  last_message_at: string | null;
  num_unread: number;
  history_complete: boolean;
  newest_synced_message_external_id: string | null;
  oldest_synced_message_external_id: string | null;
  last_synced_at: string | null;
  issues: string[];
  /** The OWNER's side of the conversation — the other half of `counterpart`. Null where
   *  the participant list names nobody we can resolve (a group thread, or an owner who
   *  has never been seen posting in a community we track). */
  owner: DmCounterpart | null;
}

export interface DmMessageRow {
  id: number;
  external_id: string;
  /** `out` (owner sent), `in` (counterpart sent), or `unknown` — the last being a real
   *  data fault the transcript shows rather than guessing past. */
  direction: string;
  sender_external_id: string | null;
  recipient_external_id: string | null;
  content: string | null;
  /** Derived from `videos`; kept because the column has always read it. */
  has_video: boolean;
  /** The descriptors `has_video` is derived from, and the attachments it never covered
   *  (#161). This surface answers "what did we actually mirror for this conversation",
   *  and a boolean cannot show that a message was an image at all. Same two columns
   *  the other two DM routes send, so the same two types. */
  videos: DmVideo[] | null;
  attachments: DmAttachment[] | null;
  created_at: string | null;
}

interface InboxPageWire extends ListEnvelope<DmInbox> {}
interface ChannelPageWire extends ListEnvelope<DmChannelRow> {
  user_id: number;
}
interface MessagePageWire extends ListEnvelope<DmMessageRow> {
  user_id: number;
  channel_id: number;
  channel: DmChannelRow;
}

/** Every owner with a mirrored inbox, or with consent and nothing arrived yet. */
export async function fetchDmInboxes(): Promise<DmInbox[]> {
  const body = await apiGet<InboxPageWire>('/dm-inboxes');
  return body.items ?? [];
}

/** One inbox's conversations, newest first. `issuesOnly` narrows to the broken ones. */
export async function fetchDmChannels(
  userId: number,
  opts: { limit?: number; offset?: number; issuesOnly?: boolean; q?: string } = {},
): Promise<{ channels: DmChannelRow[]; total: number }> {
  const p = new URLSearchParams();
  if (opts.limit) p.set('limit', String(opts.limit));
  if (opts.offset) p.set('offset', String(opts.offset));
  if (opts.issuesOnly) p.set('issues_only', 'true');
  if (opts.q?.trim()) p.set('q', opts.q.trim());
  const qs = p.toString();
  const body = await apiGet<ChannelPageWire>(
    `/dm-inboxes/${userId}/channels` + (qs ? `?${qs}` : ''),
  );
  return { channels: body.items ?? [], total: body.total ?? 0 };
}

/** One conversation, oldest first — it is read to follow a thread or to find where a
 *  backfill stopped, and both of those read forwards. */
export async function fetchDmMessages(
  userId: number,
  channelId: number,
  opts: { limit?: number; offset?: number } = {},
): Promise<{ messages: DmMessageRow[]; total: number; channel: DmChannelRow }> {
  const p = new URLSearchParams();
  if (opts.limit) p.set('limit', String(opts.limit));
  if (opts.offset) p.set('offset', String(opts.offset));
  const qs = p.toString();
  const body = await apiGet<MessagePageWire>(
    `/dm-inboxes/${userId}/channels/${channelId}/messages` + (qs ? `?${qs}` : ''),
  );
  return { messages: body.items ?? [], total: body.total ?? 0, channel: body.channel };
}
